import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import quarterOfYear from 'dayjs/plugin/quarterOfYear'

dayjs.extend(quarterOfYear)

export interface DateRangePreset {
  label: string
  getRange: (now?: Dayjs) => { from: Date; to: Date }
}

// Computed from "now" on each call so the month/quarter labels stay current.
export const getDateRangePresets = (): DateRangePreset[] => {
  const months: DateRangePreset[] = [
    {
      label: 'Current month',
      getRange: (now = dayjs()) => ({
        from: now.startOf('month').toDate(),
        to: now.endOf('month').toDate()
      })
    },
    {
      label: 'Prev month',
      getRange: (now = dayjs()) => {
        const m = now.subtract(1, 'month')
        return {
          from: m.startOf('month').toDate(),
          to: m.endOf('month').toDate()
        }
      }
    },
    {
      label: 'Current week',
      getRange: (now = dayjs()) => ({
        from: now.startOf('week').toDate(),
        to: now.endOf('week').toDate()
      })
    }
  ]

  const rolling: DateRangePreset[] = [
    {
      label: 'Past 30 days',
      getRange: (now = dayjs()) => ({
        from: now.subtract(29, 'day').toDate(),
        to: now.toDate()
      })
    },
    {
      label: 'Past 3 months',
      getRange: (now = dayjs()) => ({
        from: now.subtract(3, 'month').toDate(),
        to: now.toDate()
      })
    },
    {
      label: 'Past 6 months',
      getRange: (now = dayjs()) => ({
        from: now.subtract(6, 'month').toDate(),
        to: now.toDate()
      })
    }
  ]

  // Q1–Q4 of the current year, e.g. "Q1 2026". Only quarters that have already
  // started — future quarters of this year aren't relevant yet.
  const year = dayjs().year()
  const currentQuarter = dayjs().quarter()
  const quarters: DateRangePreset[] = [1, 2, 3, 4]
    .filter(q => q <= currentQuarter)
    .map(q => ({
      label: `Q${q} ${year}`,
      getRange: () => {
        const base = dayjs().year(year).quarter(q)
        return {
          from: base.startOf('quarter').toDate(),
          to: base.endOf('quarter').toDate()
        }
      }
    }))

  return [...months, ...rolling, ...quarters]
}

// Max span of a single backfill job (backend enforces the same cap).
export const MAX_BACKFILL_SPAN_DAYS = 90

// Last 30 days ending yesterday — the daily run already covers today.
export const getDefaultBackfillRange = (): { from: Date; to: Date } => {
  const yesterday = dayjs().subtract(1, 'day')
  return {
    from: yesterday.subtract(29, 'day').toDate(),
    to: yesterday.toDate()
  }
}
